import React, { FC, useEffect, useState } from "react";
import dayjs from "dayjs";
import Sheet from "@mui/joy/Sheet";
import Button from "@mui/joy/Button";
import Timeline from "./Components/Timeline";
import IconButton from "@mui/joy/IconButton";
import Skeleton from "react-loading-skeleton";
import CssBaseline from "@mui/joy/CssBaseline";
import ModalForm from "../../../common/ModalForm";
import LoadingIcon from "../../../common/LoadingIcon";
import ToastMessage from "../../../common/ToastMessage";
import FeedbackModal from "./TicketModal/FeedbackModal";
import SubTicketTable from "./SubTicket/SubTicketTable";
import useScreenWidth from "../../../store/useScreenWidth";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import CreateRoundedIcon from "@mui/icons-material/CreateRounded";
import CustomizedInputsStyled from "./Components/CustomizedInputStyle";
import useAddTicket from "../../../store/ReactQueries/tickets/useAddTickets";
import useTicketById from "../../../store/ReactQueries/tickets/useTicketById";
import useUpdateTicket from "../../../store/ReactQueries/tickets/useUpdateTicket";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import useUpdateRecTicket from "../../../store/ReactQueries/recurringTickets/useUpdateRecTicket";
import useAddAssetActions from "../../../store/ReactQueries/assets/assetActions/useAddAssetAction";
import { EndPoints } from "../../../utils/apiEndpoints";
import { useNavigate, useParams } from "react-router-dom";
import { AddTicketItems } from "./Components/AddTicketItems";
import { FieldErrors } from "../../../domain/helpers/Errors";
import { validateSchema } from "../../../common/ValidateSchema";
import { AddTicketColumns } from "./Components/AddTicketColumns";
import { darkModeBackgroudInputs } from "../../../utils/constants";
import { useLanguageStore } from "../../../store/useLanguageStore";
import { useDarkModeStore } from "../../../store/useDarkModeStore";
import { TicketInterface } from "../../../models/TicketModule/Ticket";
import { TicketFinancialType } from "../../../enums/ticketFinancialType";
import { ticketSchema } from "../../../domain/ValidationSchemas/Ticket/ticket";
import { assetActionSchema } from "../../../domain/ValidationSchemas/Asset/assetAction";
import { AssetActionInterface } from "../../../models/AssetModel/AssetAction/AssetAction";

export interface AddTicketModal {
  add: boolean;
  edit: boolean;
  view: boolean;
}
export const showColumnModal = ["title", "assetActionTypeId", "amount", "date", "description"];

export const initialTicket: TicketInterface = {
  id: "",
  title: "",
  statusId: "",
  categoryId: "",
  interventionTypeId: "",
  locationId: "",
  priorityId: "",
  customerId: "",
  contractorId: "",
  assignedTo: null,
  assignedToId: null,
  asset: null,
  assetId: null,
  dueDate: undefined,
  description: "",
  departmentId: "",
  formImages: [],
  images: [],
  ticketFinancialType: TicketFinancialType.Nonbillable ?? 0,
  financialAssignedToId: "",
  financialAssignedTo: null,
  companyId: "",
  company: null,
  isRecurring: false,
  evidenceNeeded: false,
  recurringPeriod: undefined,
  recurringStartTime: undefined,
  isMasterTicket: false,
  masterTicketId: undefined,
  subTickets: [],
};

export const initialAssetAction: AssetActionInterface = {
  id: "",
  amount: 0,
  title: "",
  assetId: "",
  description: "",
  date: undefined,
  assetActionTypeId: "",
};

export const sheetCss = {
  my: 1,
  py: 4,
  px: 4,
  gap: 0,
  width: "90%",
  display: "flex",
  borderRadius: "md",
  flexDirection: "column",
  boxShadow: "8px 8px 30px rgba(0, 0, 0, 0.2)",
};

export const saveBtnStyle = {
  width: 85,
  color: "white",
  fontWeight: 700,
  fontSize: "16px",
  background: "var(--btn-color)",
  "&:hover": {
    background: "var(--btn-color)",
    opacity: 0.7,
  },
};

interface AddTicketProps {
  view?: boolean;
  isRecurringTicket?: boolean;
}

export const AddTicket: FC<AddTicketProps> = ({ view = false, isRecurringTicket = false }) => {
  const navigate = useNavigate();
  const addNewTicket = useAddTicket();
  const isSmallScreen = useScreenWidth();

  const { darkMode } = useDarkModeStore();
  const { language } = useLanguageStore();

  const { id: ticketId, readFeedback } = useParams<{ id: string; readFeedback: string }>();
  const { data: ticketById, error: ticketByIdError, isLoading, refetch } = useTicketById(ticketId || "");

  const isReadFeedback = readFeedback === "true";
  const navigateEndpoint = isRecurringTicket ? EndPoints.RECURRINGTICKETS : EndPoints.TICKETS;

  const updateTicket = useUpdateTicket(1, 20);
  const addAssetAction = useAddAssetActions(1, 20);
  const updateRecurringTicket = useUpdateRecTicket(1, 20);

  const [subTickets, setSubTickets] = useState<TicketInterface[]>([]);
  const [hasSubTicketsError, setHasSubTicketsError] = useState<boolean>(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [form, setForm] = useState<TicketInterface>(initialTicket);
  const [openFeedback, setOpenFeedback] = useState<boolean>(false);
  const [disableSaveBtn, setDisableBtnSave] = useState<boolean>(false);
  const [errorsAssetAction, setErrorsAssetAction] = useState<FieldErrors>({});
  const [modal, setModal] = useState<AddTicketModal>({ add: false, edit: false, view: false });
  const [formAssetAction, setFormAssetAction] = useState<AssetActionInterface>(initialAssetAction);

  const handleError = (error: unknown, reject: (reason?: any) => void) => {
    if (error instanceof Error) ToastMessage.Failed(error.message);
    else ToastMessage.Failed();
    reject(error);
  };

  const handleSave = async () => {
    const isValid = validateSchema(ticketSchema, form);
    setErrors(isValid.errors);
    if (!isValid.result) return;

    if (form.status?.isCompleted && form.assetId) {
      setModal({ ...modal, add: true, edit: false, view: false });
    } else saveTicket();
  };

  const handleFormNewItem = () => {
    let upForm: TicketInterface = {
      id: form.id,
      title: form.title,
      images: form.images,
      dueDate: form.dueDate,
      statusId: form.statusId,
      categoryId: form.categoryId,
      locationId: form.locationId,
      priorityId: form.priorityId,
      customerId: form.customerId,
      formImages: form.formImages,
      description: form.description,
      contractorId: form.contractorId,
      departmentId: form.departmentId,
      interventionTypeId: form.interventionTypeId,
      companyId: form.companyId,
      isRecurring: form.isRecurring ?? false,
      evidenceNeeded: form.evidenceNeeded ?? false,
      isMasterTicket: form.isMasterTicket ?? false,
      // masterTicketId: form.masterTicketId,
    };
    if (form.isRecurring) {
      upForm.recurringPeriod = form.recurringPeriod;
      upForm.recurringStartTime = form.recurringStartTime;
    }
    if (form.assignedToId) {
      upForm.assignedToId = form.assignedToId;
    }
    if (form.assetId) {
      upForm.assetId = form.assetId;
    }
    if (form.ticketFinancialType === TicketFinancialType.Billable) {
      upForm.ticketFinancialType = TicketFinancialType.Billable;
      upForm.financialAssignedToId = form.financialAssignedToId;
    }
    if (form.isMasterTicket) {
      delete upForm.masterTicketId;
    } else {
      upForm.masterTicketId = form.masterTicketId;
    }
    return upForm;
  };

  const saveTicket = async () => {
    setDisableBtnSave(true);
    try {
      await new Promise<void>((resolve, reject) => {
        let upForm: TicketInterface = handleFormNewItem();
        if (upForm.id === "") {
          const { id, ...formData } = upForm;
          addNewTicket.mutate(formData, {
            onError: (error) => handleError(error, reject),
          });
        } else {
          let updateForm = { ...upForm };
          if (updateForm.formImages === null) {
            delete updateForm.formImages;
          }
          if (isRecurringTicket) {
            updateRecurringTicket.mutate(upForm, {
              onError: (error: unknown) => handleError(error, reject),
            });
          } else {
            updateTicket.mutate(upForm, {
              onError: (error: unknown) => handleError(error, reject),
            });
          }
        }

        if (subTickets && subTickets.length > 0) {
          subTickets.forEach((subTicket) => {
            if (!subTicket.id) {
              const { id, ...formData } = subTicket;
              addNewTicket.mutate(formData, {
                onError: (error) => handleError(error, resolve),
              });
            }
          });
        }

        ToastMessage.Success();
        resolve();
        navigate(navigateEndpoint);
      });
    } catch (ex: unknown) {
      if (ex instanceof Error) {
        console.log(ex.message);
      } else {
        console.log("An unknown error occurred");
      }
    }
    setTimeout(() => {
      setDisableBtnSave(false);
    }, 300);
  };

  const handleFeedback = () => setOpenFeedback(true);
  const handleCloseFeedback = () => setOpenFeedback(false);

  const handleSaveAssetAction = async (assetid: string | undefined | null) => {
    let formA = { ...formAssetAction };
    formA.assetId = assetid || "";

    const isValid = validateSchema(assetActionSchema, formA);

    setErrorsAssetAction(isValid.errors);
    if (!isValid.result) return;

    saveTicket();

    const { id, title, assetActionTypeId, amount, date, description, assetId } = formA;

    const upForm: AssetActionInterface = {
      id,
      date,
      title,
      amount,
      assetId,
      description,
      assetActionTypeId,
    };

    if (upForm.id === "") {
      const { id, ...formData } = upForm;
      addAssetAction.mutate(formData, {
        onError: (error) => ToastMessage.Failed(error.message),
      });
    }
    setModal({ ...modal, add: false, edit: false, view: false });
  };

  useEffect(() => {
    if (isReadFeedback) setOpenFeedback(true);
  }, [isReadFeedback]);

  useEffect(() => {
    if (ticketByIdError) {
      ToastMessage.Failed(`Error: ${ticketByIdError.message}`);
      navigate(-1);
    }
  }, [ticketByIdError]);

  useEffect(() => {
    if (ticketId && ticketById) setForm(ticketById as TicketInterface);
  }, [ticketId, ticketById]);

  useEffect(() => {
    setLoading(true);

    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const HrLine = () => <hr className={darkMode ? "text-white opacity-100" : ""} />;
  const handleNavigate = () => navigate(`${navigateEndpoint}/edit/${form.id}`);

  return (
    <div className="w-100 d-flex justify-content-center ">
      <CssBaseline />
      {isLoading ? (
        <LoadingIcon width={50} />
      ) : (
        <div className="d-flex flex-column align-items-center gap-2 w-100">
          <Sheet
            sx={{
              ...sheetCss,
              background: darkMode ? "var(--dark-mode-bg)" : "white",
            }}
          >
            <div
              className="mb-0 d-flex align-items-center"
              style={{
                color: darkMode ? "white" : "rgba(0, 0, 0, 0.6)",
                fontSize: "20px",
              }}
            >
              <ConfirmationNumberOutlinedIcon className="me-2" />
              {language["Ticket"]}
              {form.ticketNo ? (
                loading ? (
                  <Skeleton style={{ marginLeft: 10, marginBottom: 5, width: 145, height: 24 }} />
                ) : (
                  ` ${form.ticketNo}`
                )
              ) : (
                ` ${language["Add"]}`
              )}
              {view && (
                <IconButton
                  className={`ms-2 ${darkMode && "text-white"}`}
                  title={language["EditTicket"]}
                  onClick={handleNavigate}
                >
                  <CreateRoundedIcon sx={{ width: 18, height: 18 }} />
                </IconButton>
              )}
              {form.id !== "" ? (
                loading ? (
                  <div className="ms-auto">
                    <Skeleton style={{ width: 36, height: 36 }} />
                  </div>
                ) : (
                  <IconButton
                    title={language["Feedback"]}
                    className="ms-auto"
                    style={darkMode ? { color: "white" } : {}}
                    onClick={handleFeedback}
                  >
                    <ChatOutlinedIcon />
                  </IconButton>
                )
              ) : null}
            </div>

            <hr style={darkMode ? { color: "white", opacity: 1 } : {}} />

            <div className="p-2">
              {AddTicketItems?.map(({ id, data, section }) => (
                <div key={id + "AddTicketItems"}>
                  <div
                    className=" mt-3 mb-3"
                    style={{
                      borderBottom: `2px solid ${darkMode ? "white" : "#D8DDE2"}`,
                      color: darkMode ? "white" : "rgba(0,0,0,.8)",
                    }}
                  >
                    {language[section]}
                  </div>
                  <form className={`${isSmallScreen ? "d-flex flex-column" : "module-form-grid-ticket"} gap-2`}>
                    {data?.map(({ id, name, label, type }, index) => (
                      <CustomizedInputsStyled
                        name={name}
                        type={type}
                        form={form}
                        view={view}
                        label={label}
                        errors={errors}
                        loading={loading}
                        setForm={setForm}
                        key={id + index + "-CustomizedInputsStyled"}
                        hasMasterTicketId={ticketById?.masterTicketId !== null}
                      />
                    ))}
                  </form>
                </div>
              ))}
            </div>
            {form.isMasterTicket && (
              <SubTicketTable
                view={view}
                subTickets={subTickets}
                masterTicketId={form.id}
                setSubTickets={setSubTickets}
                setHasSubTicketsError={setHasSubTicketsError}
              />
            )}
            {ticketById && ticketById.ticketTimelines && ticketById.ticketTimelines.length > 0 && (
              <React.Fragment>
                <Timeline ticketById={ticketById} />
                <HrLine />
              </React.Fragment>
            )}
            <div className="d-flex flex-row justify-content-end gap-2">
              <Button
                sx={{
                  fontWeight: 700,
                  fontSize: "16px",
                  border: `1px solid var(--btn-color)`,
                  color: darkMode ? "white" : "var(--btn-color)",
                  background: darkMode ? darkModeBackgroudInputs : "white",
                  "&:hover": {
                    background: darkMode ? darkModeBackgroudInputs : "white",
                    opacity: 0.7,
                  },
                }}
                onClick={() => navigate(navigateEndpoint)}
              >
                {language["Cancel"]}
              </Button>
              {!view && (
                <Button sx={saveBtnStyle} onClick={handleSave} disabled={hasSubTicketsError}>
                  {disableSaveBtn ? <LoadingIcon width={22} /> : language["Save"]}
                </Button>
              )}
            </div>
            {ticketById && (
              <div
                className={`position-absolute d-flex flex-row gap-1 ${darkMode ? "text-white" : "text-muted"}`}
                style={{ bottom: 10, left: 10, fontSize: 11 }}
              >
                <span>
                  {language["CreatedAt"]}:{" "}
                  {ticketById.createdDate ? dayjs(ticketById.createdDate).format("DD/MM/YYYY HH:mm") : ""}
                </span>

                {ticketById.author && (
                  <span>
                    {language["by"]} {`${ticketById.author.firstname ?? ""} ${ticketById.author.lastname ?? ""}`}
                  </span>
                )}
              </div>
            )}
          </Sheet>
          {
            <ModalForm
              columns={AddTicketColumns.filter((col) => showColumnModal.some((item) => item === col.field)).sort(
                (a, b) => showColumnModal.indexOf(a.field) - showColumnModal.indexOf(b.field)
              )}
              open={modal}
              form={formAssetAction}
              errors={errorsAssetAction}
              setOpen={setModal}
              setForm={setFormAssetAction}
              handleSave={() => handleSaveAssetAction(form?.assetId)}
            />
          }
        </div>
      )}
      {openFeedback && <FeedbackModal ticketId={form.id || ""} open={openFeedback} handleClose={handleCloseFeedback} />}
    </div>
  );
};
export default AddTicket;
